import { runInAction } from 'mobx'
import { store } from '@store/Store'
import { handleUnauthorized } from '@utils/apiHelpers'
import { applyOperatorTransferNotification } from '@utils/operatorTransferNotifications'

/**
 * Optional REST fallback when JWT lacks operator.*.notifications.
 * Disabled by default — known endpoints currently 404 on production.
 * Set VITE_TRANSFER_PENDING_POLL=1 to re-enable once backend adds an API.
 */
const PENDING_PATHS = [
  '/api/operator/dialog-transfer/pending',
  '/api/operator/dialog-transfers?status=PENDING',
]

const POLL_MS = 5000

function pollEnabled(): boolean {
  try {
    return String(import.meta.env?.VITE_TRANSFER_PENDING_POLL ?? '') === '1'
  } catch {
    return false
  }
}

function asTransferRequestedEvents(json: unknown): unknown[] {
  if (!json || typeof json !== 'object') return []
  const root = json as Record<string, unknown>
  const raw =
    root.data ??
    root.items ??
    root.transfers ??
    root.requests ??
    (Array.isArray(json) ? json : null)

  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (!item || typeof item !== 'object') return null
        const row = item as Record<string, unknown>
        if (String(row.type ?? '').includes('transfer')) return row
        return {
          type: 'dialog.transfer.requested',
          payload: row.payload && typeof row.payload === 'object' ? row.payload : row,
        }
      })
      .filter(Boolean)
  }

  if (raw && typeof raw === 'object') {
    const row = raw as Record<string, unknown>
    if (Array.isArray(row.items)) return asTransferRequestedEvents({ data: row.items })
    return [
      {
        type: 'dialog.transfer.requested',
        payload: row.payload && typeof row.payload === 'object' ? row.payload : row,
      },
    ]
  }

  return []
}

export function startIncomingTransferPolling(
  baseUrl: string,
  headers: Record<string, string>,
  operatorId: number,
): () => void {
  if (!pollEnabled()) {
    console.error(
      `[transfer] BLOCKED: JWT has no operator.${operatorId}.notifications and no pending REST API. ` +
        'Backend must add operator.{id}.notifications to POST /api/token JWT `subs` ' +
        '(Berlin TZ — channels come from auth, not request body).',
    )
    return () => {}
  }

  const base = baseUrl.replace(/\/$/, '')
  let stopped = false
  let workingPath: string | null = null
  let inFlight = false
  let pathIndex = 0

  const tick = async () => {
    if (stopped || inFlight) return
    inFlight = true
    try {
      const paths = workingPath ? [workingPath] : PENDING_PATHS.slice(pathIndex)
      for (const path of paths) {
        try {
          const res = await fetch(`${base}${path}`, {
            headers: { Accept: 'application/json', ...headers },
          })
          if (handleUnauthorized(res)) return
          if (res.status === 404 || res.status === 405) {
            pathIndex = Math.min(pathIndex + 1, PENDING_PATHS.length)
            continue
          }
          if (!res.ok) continue
          const json = await res.json().catch(() => null)
          workingPath = path
          const events = asTransferRequestedEvents(json)
          runInAction(() => {
            for (const event of events) {
              applyOperatorTransferNotification(event, operatorId, store.operatorTransfers)
            }
          })
          break
        } catch {
          /* ignore */
        }
      }
    } finally {
      inFlight = false
    }
  }

  void tick()
  const id = window.setInterval(() => void tick(), POLL_MS)
  return () => {
    stopped = true
    window.clearInterval(id)
  }
}
